import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../utils/config';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ReportJobs = ({ reportKey }) => {
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createParams, setCreateParams] = useState({});
  const [createFormat, setCreateFormat] = useState('csv');

  useEffect(() => {
    fetchJobs();
    // Poll for job updates every 5 seconds
    const interval = setInterval(fetchJobs, 5000);
    return () => clearInterval(interval);
  }, [reportKey]);

  const fetchJobs = async () => {
    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const headers = {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      const params = reportKey ? { report: reportKey } : {};
      const response = await axios.get(API_ENDPOINTS.REPORTS_JOBS_LIST, {
        headers,
        params
      });

      // Handle standardized response format: { status: "ok", data: [...] }
      // or direct array/object format
      let data = [];
      if (response.data?.status === 'ok' && response.data?.data) {
        data = Array.isArray(response.data.data) ? response.data.data : [];
      } else if (Array.isArray(response.data)) {
        data = response.data;
      } else {
        data = response.data?.results || response.data?.data || [];
      }
      
      // Sort by created_at descending
      data.sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });

      setJobs(data);
    } catch (err) {
      const errorMsg = err?.response?.data?.message || 
                     err?.response?.data?.detail || 
                     err?.message || 
                     'Failed to fetch report jobs';
      setError(errorMsg);
      console.error('Error fetching report jobs:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateJob = async () => {
    setIsCreating(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      await axios.post(
        API_ENDPOINTS.REPORTS_JOBS_CREATE,
        {
          report_key: reportKey,
          params: createParams,
          format: createFormat
        },
        { headers }
      );

      // Reset form and refresh jobs
      setCreateParams({});
      setCreateFormat('csv');
      fetchJobs();
    } catch (err) {
      const errorMsg = err?.response?.data?.message || 
                     err?.response?.data?.detail || 
                     err?.message || 
                     'Failed to create report job';
      setError(errorMsg);
      console.error('Error creating report job:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancelJob = async (jobId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      await axios.post(API_ENDPOINTS.REPORTS_JOBS_CANCEL(jobId), {}, { headers });
      fetchJobs();
    } catch (err) {
      const errorMsg = err?.response?.data?.message || 
                     err?.response?.data?.detail || 
                     err?.message || 
                     'Failed to cancel job';
      alert(errorMsg);
      console.error('Error canceling job:', err);
    }
  };

  const handleDownload = async (jobId, jobFormat) => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      const response = await axios.get(API_ENDPOINTS.REPORTS_JOBS_DOWNLOAD(jobId), {
        headers,
        responseType: 'blob'
      });

      // Determine file extension from format or content type
      const format = jobFormat || 'csv';
      const extension = format === 'xlsx' ? 'xlsx' : format === 'json' ? 'json' : 'csv';
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_${jobId}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const errorMsg = err?.response?.data?.message || 
                     err?.response?.data?.detail || 
                     err?.message || 
                     'Failed to download report';
      alert(errorMsg);
      console.error('Error downloading report:', err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'succeeded':
        return 'text-success';
      case 'failed':
        return 'text-destructive';
      case 'running':
        return 'text-primary';
      case 'pending':
        return 'text-warning';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'succeeded':
        return 'CheckCircle';
      case 'failed':
        return 'XCircle';
      case 'running':
        return 'Loader2';
      case 'pending':
        return 'Clock';
      default:
        return 'FileText';
    }
  };

  return (
    <div className="space-y-6">
      {/* Create New Job */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Generate Report</h3>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Format</label>
            <select
              value={createFormat}
              onChange={(e) => setCreateFormat(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="csv">CSV</option>
              <option value="xlsx">Excel (XLSX)</option>
              <option value="json">JSON</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Parameters (JSON)</label>
            <textarea
              value={JSON.stringify(createParams, null, 2)}
              onChange={(e) => {
                try {
                  setCreateParams(JSON.parse(e.target.value));
                } catch {
                  // Invalid JSON, ignore
                }
              }}
              placeholder='{"start_date": "2025-01-01", "end_date": "2025-01-31"}'
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
              rows={4}
            />
          </div>
          <Button
            onClick={handleCreateJob}
            disabled={isCreating || !reportKey}
            iconName="Download"
            iconPosition="left"
          >
            {isCreating ? 'Creating...' : 'Generate Report'}
          </Button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <div className="flex items-center space-x-2 text-destructive">
            <Icon name="AlertCircle" size={20} />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Jobs List */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Generated Reports</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchJobs}
            iconName="RefreshCw"
            iconPosition="left"
          >
            Refresh
          </Button>
        </div>

        {isLoading && jobs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">Loading jobs...</div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">No report jobs found</div>
        ) : (
          <div className="space-y-4">
            {jobs.map((job) => (
              <div
                key={job.id}
                className="p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <Icon
                        name={getStatusIcon(job.status)}
                        size={20}
                        className={getStatusColor(job.status)}
                      />
                      <span className="font-medium text-foreground">
                        Report #{job.id}
                      </span>
                      <span className={`text-sm ${getStatusColor(job.status)}`}>
                        {job.status?.toUpperCase() || 'UNKNOWN'}
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      {job.report_key && (
                        <div>Report: {job.report_key}</div>
                      )}
                      {job.format && (
                        <div>Format: {job.format.toUpperCase()}</div>
                      )}
                      {job.created_at && (
                        <div>Created: {new Date(job.created_at).toLocaleString()}</div>
                      )}
                      {job.error && (
                        <div className="text-destructive">Error: {job.error}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {job.status === 'pending' || job.status === 'running' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCancelJob(job.id)}
                      >
                        Cancel
                      </Button>
                    ) : null}
                    {job.status === 'succeeded' && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleDownload(job.id, job.format)}
                        iconName="Download"
                        iconPosition="left"
                      >
                        Download
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportJobs;

